import React from "react";
import { PartyInfo, PatientInfo, EmployerInfo } from "../xml-document-parser.service";

interface PartyInfoSectionProps {
	title: string;
	party?: PartyInfo | PatientInfo | EmployerInfo;
	compact?: boolean;
}

export const PartyInfoSection: React.FC<PartyInfoSectionProps> = ({ title, party, compact = false }) => {
	if (!party) return null;

	const formatAddress = () => {
		const parts = [];
		if (party.street) parts.push(party.street);
		if (party.poBox) parts.push(`Postfach ${party.poBox}`);
		if (party.zip || party.city) {
			parts.push(`${party.zip || ''} ${party.city || ''}`.trim());
		}
		if (party.country) parts.push(party.country);
		return parts;
	};

	const formatName = () => {
		if ('companyName' in party && party.companyName) {
			return party.companyName;
		}
		if ('familyName' in party && party.familyName) {
			const patient = party as PatientInfo;
			return `${patient.familyName} ${patient.givenName || ''}`.trim();
		}
		return party.name || '';
	};

	const formatTitleAndName = () => {
		const name = formatName();
		if (party.title && party.salutation) {
			return `${party.salutation} ${party.title} ${name}`;
		} else if (party.salutation) {
			return `${party.salutation} ${name}`;
		} else if (party.title) {
			return `${party.title} ${name}`;
		}
		return name;
	};

	if (compact) {
		return (
			<div className="text-sm">
				<span className="font-medium">{title}:</span>{' '}
				{formatTitleAndName()}
				{party.department && <span> · {party.department}</span>}
				{formatAddress().length > 0 && <span> · {formatAddress().join(' · ')}</span>}
			</div>
		);
	}

	return (
		<div className="mb-4">
			<h3 className="text-sm font-semibold text-gray-700 mb-1">{title}</h3>
			<div className="text-sm">
				<div className="font-medium">{formatTitleAndName()}</div>
				{party.department && <div>{party.department}</div>}
				{formatAddress().map((line, idx) => (
					<div key={idx}>{line}</div>
				))}
				{party.phone && <div>Tel: {party.phone}</div>}
				{party.fax && <div>Fax: {party.fax}</div>}
				{party.email && <div>{party.email}</div>}
			</div>
		</div>
	);
};

interface AddressBlockProps {
	party?: PartyInfo | PatientInfo | EmployerInfo;
	showLabels?: boolean;
}

export const AddressBlock: React.FC<AddressBlockProps> = ({ party, showLabels = false }) => {
	if (!party) return null;

	const formatName = () => {
		if ('companyName' in party && party.companyName) {
			return party.companyName;
		}
		if ('familyName' in party && party.familyName) {
			const patient = party as PatientInfo;
			return `${patient.givenName || ''} ${patient.familyName}`.trim();
		}
		return party.name || '';
	};

	return (
		<div className="text-sm">
			{party.salutation && <div>{party.salutation}</div>}
			<div className="font-medium">{formatName()}</div>
			{party.department && <div>{party.department}</div>}
			{'subaddressing' in party && party.subaddressing && <div>{party.subaddressing}</div>}
			{party.street && <div>{party.street}</div>}
			{party.poBox && <div>Postfach {party.poBox}</div>}
			{(party.zip || party.city) && (
				<div>
					{showLabels && party.stateCode && <span>{party.stateCode} </span>}
					{party.zip} {party.city}
				</div>
			)}
			{party.country && <div>{party.country}</div>}
		</div>
	);
};

interface InfoGridProps {
	items: Array<{ label: string; value?: string | number | null }>;
	columns?: number;
}

export const InfoGrid: React.FC<InfoGridProps> = ({ items, columns = 2 }) => {
	const filteredItems = items.filter(item => item.value !== undefined && item.value !== null && item.value !== '');
	
	return (
		<div className={`grid grid-cols-${columns} gap-x-4 gap-y-1 text-sm`}>
			{filteredItems.map((item, idx) => (
				<div key={idx} className="flex">
					<span className="text-gray-600 min-w-[120px]">{item.label}:</span>
					<span className="font-medium">{item.value}</span>
				</div>
			))}
		</div>
	);
};